import { useState, useEffect, useCallback } from 'react';
import type { AuthResponse, AvailableSalon } from '../api/authApi';

/**
 * Hook to get the current user's salonId from localStorage.
 * Returns null if the user doesn't have a salon assigned.
 * Listens to storage events to sync across tabs.
 * 
 * @returns The active salon ID for the current user, or null if not assigned
 */
export const useSalonId = (): string | null => {
    const [salonId, setSalonId] = useState<string | null>(null);

    useEffect(() => {
        const loadSalonId = () => {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    const user: AuthResponse = JSON.parse(userStr);
                    setSalonId(user.salonId || null);
                } catch (error) {
                    console.error('Error parsing user from localStorage:', error);
                    setSalonId(null);
                }
            } else {
                setSalonId(null);
            }
        };

        loadSalonId();

        // Listen for storage changes (e.g., from other tabs or SalonSelector)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'user') {
                loadSalonId();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    return salonId;
};

/**
 * Hook with loading state for salonId.
 * Useful when you need to distinguish between "loading" and "no salon assigned".
 */
export const useSalonIdWithLoading = (): { salonId: string | null; isLoading: boolean } => {
    const [salonId, setSalonId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user: AuthResponse = JSON.parse(userStr);
                setSalonId(user.salonId || null);
            } catch (error) {
                console.error('Error parsing user from localStorage:', error);
            }
        }
        setIsLoading(false);
    }, []);

    return { salonId, isLoading };
};

/**
 * Get salonId synchronously (for use outside of React components).
 * Returns null if no user is logged in or has no salonId.
 */
export const getSalonIdSync = (): string | null => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
        try {
            const user: AuthResponse = JSON.parse(userStr);
            return user.salonId || null;
        } catch (error) {
            console.error('Error parsing user from localStorage:', error);
        }
    }
    return null;
};

/**
 * Get available salons for the current user.
 * Returns an empty array if no user is logged in.
 */
export const getAvailableSalons = (): AvailableSalon[] => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
        try {
            const user: AuthResponse = JSON.parse(userStr);
            return user.availableSalons || [];
        } catch (error) {
            console.error('Error parsing user from localStorage:', error);
        }
    }
    return [];
};

/**
 * Hook to get both current salon and available salons.
 * Provides function to switch salons.
 */
export const useSalonContext = () => {
    const [salonId, setSalonId] = useState<string | null>(null);
    const [availableSalons, setAvailableSalons] = useState<AvailableSalon[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = () => {
            const userStr = localStorage.getItem('user');
            if (userStr) {
                try {
                    const user: AuthResponse = JSON.parse(userStr);
                    setSalonId(user.salonId || null);
                    setAvailableSalons(user.availableSalons || []);
                } catch (error) {
                    console.error('Error parsing user from localStorage:', error);
                }
            }
            setIsLoading(false);
        };

        loadData();
    }, []);

    const switchSalon = useCallback((newSalonId: string) => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user: AuthResponse = JSON.parse(userStr);
                user.salonId = newSalonId;
                localStorage.setItem('user', JSON.stringify(user));
                setSalonId(newSalonId);
                // Trigger a page reload to refresh all data
                window.location.reload();
            } catch (error) {
                console.error('Error switching salon:', error);
            }
        }
    }, []);

    const currentSalon = availableSalons.find(s => s.id === salonId);

    return {
        salonId,
        currentSalon,
        availableSalons,
        isLoading,
        hasMultipleSalons: availableSalons.length > 1,
        switchSalon
    };
};

